import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, CheckCircle, RefreshCw, ArrowLeft, Brain, Sparkles, AlertCircle, Shield } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { auth } from '@/config/firebase'
import { toast } from 'react-hot-toast'
import LoadingSpinner from '@components/UI/LoadingSpinner'

const EmailVerification = () => {
  const { currentUser, verifyEmail, getUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const navigate = useNavigate()
  const currentUserRef = useRef(currentUser)
  const hasNavigatedRef = useRef(false)

  console.log('EmailVerification: Component mounted, currentUser:', currentUser?.uid, 'emailVerified:', currentUser?.emailVerified)

  // Countdown timer for resend button
  useEffect(() => {
    let timer
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  // Update ref when currentUser changes
  useEffect(() => {
    currentUserRef.current = currentUser
  }, [currentUser])

  // Check if user is already verified
  useEffect(() => {
    console.log('EmailVerification: Checking initial verification status:', currentUser?.emailVerified)
    if (currentUser?.emailVerified && !hasNavigatedRef.current) {
      console.log('EmailVerification: User already verified, reloading app')
      hasNavigatedRef.current = true
      toast.success('Email verified successfully! Reloading app...')
      window.location.reload()
    }
  }, [currentUser?.emailVerified])

  // Additional check on component mount
  useEffect(() => {
    const checkInitialStatus = async () => {
      console.log('EmailVerification: Checking initial status on mount')
      try {
        const currentFirebaseUser = auth.currentUser
        if (currentFirebaseUser) {
          await currentFirebaseUser.reload()
          const updatedUser = auth.currentUser
          console.log('EmailVerification: Initial status check:', updatedUser?.emailVerified)
          
          if (updatedUser?.emailVerified && !hasNavigatedRef.current) {
            console.log('EmailVerification: User verified on initial check, reloading app')
            hasNavigatedRef.current = true
            toast.success('Email verified successfully! Reloading app...')
            window.location.reload()
          }
        }
      } catch (error) {
        console.error('Error checking initial verification status:', error)
      }
    }
    
    checkInitialStatus()
  }, [navigate])

  // Set up a listener for email verification status changes
  useEffect(() => {
    console.log('EmailVerification: Setting up listener, currentUser:', currentUser?.uid)
    if (!currentUser) return

    console.log('EmailVerification: Starting verification check interval')
    // Check verification status every 3 seconds
    const checkVerificationInterval = setInterval(async () => {
      console.log('EmailVerification: Checking verification status...')
      try {
        if (hasNavigatedRef.current) {
          console.log('EmailVerification: Skipping check - already navigated')
          return
        }

        // Always get the current user from Firebase auth
        const currentFirebaseUser = auth.currentUser
        if (!currentFirebaseUser) {
          console.log('EmailVerification: No Firebase user found')
          return
        }

        // Reload the user to get the latest verification status
        await currentFirebaseUser.reload()
        
        // Get the updated user object after reload
        const updatedUser = auth.currentUser
        
        console.log('EmailVerification: Current verification status:', updatedUser?.emailVerified)
        
        // If email is now verified, reload the app
        if (updatedUser?.emailVerified && !hasNavigatedRef.current) {
          console.log('Email verified detected, reloading app')
          hasNavigatedRef.current = true
          clearInterval(checkVerificationInterval)
          toast.success('Email verified successfully! Reloading app...')
          window.location.reload()
        }
      } catch (error) {
        console.error('Error checking email verification status:', error)
      }
    }, 3000) // Check every 3 seconds

    // Cleanup interval on component unmount or when user changes
    return () => {
      console.log('EmailVerification: Cleaning up verification check interval')
      clearInterval(checkVerificationInterval)
    }
  }, [currentUser, navigate])

  const handleResendEmail = async () => {
    if (countdown > 0) return
    
    setResendLoading(true)
    try {
      await verifyEmail()
      setCountdown(60) // 60 second cooldown
      toast.success('Verification email sent successfully!')
    } catch (error) {
      console.error('Resend error:', error)
      toast.error('Failed to send verification email. Please try again.')
    } finally {
      setResendLoading(false)
    }
  }

  const handleRefresh = async () => {
    if (hasNavigatedRef.current) return
    
    setLoading(true)
    try {
      // Always get the current user from Firebase auth
      const currentFirebaseUser = auth.currentUser
      if (!currentFirebaseUser) {
        toast.error('No user found. Please log in again.')
        return
      }

      // Reload the Firebase user to check verification status
      await currentFirebaseUser.reload()
      
      // Get the updated user object after reload
      const updatedUser = auth.currentUser
      
      console.log('Manual refresh - verification status:', updatedUser?.emailVerified)
      
      if (updatedUser?.emailVerified && !hasNavigatedRef.current) {
        hasNavigatedRef.current = true
        toast.success('Email verified successfully! Reloading app...')
        window.location.reload()
      } else {
        toast('Email not verified yet. Please check your inbox.')
      }
    } catch (error) {
      console.error('Refresh error:', error)
      toast.error('Failed to check verification status.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Verify Your Email
          </h2>
          <p className="text-gray-600">
            We've sent a verification link to your email address
          </p>
          <div className="flex items-center justify-center mt-4 space-x-2">
            <Sparkles className="w-4 h-4 text-primary-500" />
            <span className="text-sm text-primary-600 font-medium">
              AI-Powered Platform
            </span>
          </div>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Email Display */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-100">
              <Mail className="w-4 h-4 text-primary-600 mr-2" />
              <span className="text-sm font-medium text-primary-900">
                {currentUser?.email}
              </span>
            </div>
          </div>

          {/* Instructions */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Check Your Email
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              We've sent a verification link to your email address. 
              Click the link in the email to verify your account and start using MyDigiSign.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  I've Verified My Email
                </>
              )}
            </button>

            {/* Resend Button */}
            <button
              onClick={handleResendEmail}
              disabled={resendLoading || countdown > 0}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {resendLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {countdown > 0 
                    ? `Resend in ${countdown}s` 
                    : 'Resend Verification Email'
                  }
                </>
              )}
            </button>
          </div>

          {/* Help Section */}
          <div className="mt-8 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-1">Didn't receive the email?</p>
                <ul className="space-y-1 text-xs">
                  <li>• Check your spam or junk folder</li>
                  <li>• Make sure you entered the correct email address</li>
                  <li>• Wait a few minutes before requesting a new email</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailVerification 